import { t } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import classNames from 'classnames'
import { Context, Icon, Modal, Button as ButtonScala, Loading } from 'scala'
import {
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { Button, useAudioEngine } from 'player'
import { useEffectOnce } from 'react-use'
import styles from './styles.module.scss'
import { useInstrumentsSeparateCustom } from '../../../../hooks/use-instruments-separate-custom'
import { Summary } from '../../../../types'
import { UseModalPaywall } from '../../../../hooks/event/use-modal-paywall'
import { useUpdateTrack } from '../../../../hooks/player/use-update-track'
import { SeparateCustom } from '../../../../components/separate-custom/separate-custom-instruments'
import { ToogleHifi } from '../toggle-hifi'

type Props = {
  taskId: string
  summary?: Summary
  modalPaywall: UseModalPaywall
  isLimited?: boolean
}

/**
 *
 * Custom Separate: premium + pro / premium limited
 *
 */
export const DropdownChangeOperations: React.FC<Props> = ({
  summary,
  taskId,
  modalPaywall,
  isLimited = false
}) => {
  const { i18n } = useLingui()
  const { user } = useContext(Context)
  const isReadyToPlay = useAudioEngine((p) => p.state.isReadyToPlay)
  const methods = useInstrumentsSeparateCustom()
  const {
    selectedInstruments,
    getFilterInstruments,
    loading,
    addInstrumentsPreSelected,
    getFilterMultimedia,
    clearInstrumentsSelected
  } = methods

  const ref: RefObject<any> = useRef()
  const [open, setOpen] = useState(false)
  const onToggleDrop = useCallback(() => setOpen(!open), [open])
  const { onOpen: openPremiumModal } = modalPaywall

  const isProcessing = useMemo(
    () =>
      summary?.separate?.updateInProgress ||
      (summary?.separate?.status !== 'COMPLETED' &&
        summary?.separate?.status !== 'FAILED'),
    [summary]
  )

  const newChangeSeparation = useMemo(() => {
    return user?.featureFlags?.webNewChangeSeparation
  }, [user])

  const onLockedFeatureClick = useCallback<UseModalPaywall['onOpen']>(
    (feature, automaticDisplayed, customSource) => {
      openPremiumModal(feature, automaticDisplayed, customSource)
    },
    [openPremiumModal]
  )

  const { onUpdateTrack } = useUpdateTrack({
    trackId: taskId,
    methods
  })

  const currentStems = useMemo(() => {
    const stems =
      summary?.separate.stems
        ?.map((i) => ({ id: i.stem as string }))
        .filter((i) => i?.id) ?? []
    const stemsId = stems.map((stem) => stem.id)

    return stemsId
  }, [summary])

  const instruments = getFilterInstruments()
  const multimedia = getFilterMultimedia()

  const isPro = useMemo(() => user?.subscription?.isPro, [user?.subscription])

  useEffectOnce(() => {
    addInstrumentsPreSelected(currentStems)
  })

  const [currentInstruments, setCurrentInstruments] = useState<string[]>([])

  useEffect(() => {
    if (
      currentInstruments.length ||
      !selectedInstruments.length ||
      selectedInstruments.length === 0
    ) {
      return
    }

    setCurrentInstruments(selectedInstruments)
  }, [instruments, currentInstruments, selectedInstruments])

  const isValidTracksSelected = useMemo(() => {
    // remover o filter para o original mix.
    const newSelected = [...selectedInstruments]
    const newCurrent = [...currentInstruments]

    if (!newSelected.length && !newChangeSeparation) {
      return false
    }

    if (newSelected.sort().join(',') === newCurrent.sort().join(',')) {
      return false
    }

    if (
      newChangeSeparation &&
      newSelected.length === 1 &&
      newSelected[0] === 'other' &&
      newCurrent === newSelected
    ) {
      return false
    }

    if (
      newChangeSeparation &&
      newSelected.length === 0 &&
      newCurrent.length === 0
    ) {
      return false
    }

    return true
  }, [selectedInstruments, currentInstruments, newChangeSeparation])

  const title = useMemo(() => {
    const filterStems = currentStems?.filter(
      (item, index) => currentStems?.indexOf(item) === index
    )

    const total = filterStems?.length

    if (!total) {
      return null
    }

    const labelLocalization = total === 1 ? 'track' : 'tracks'
    return i18n._(`separation_options_${total}${labelLocalization}`)
  }, [i18n, currentStems])

  const tracksSelected = useMemo(() => {
    const allInstruments = [...instruments, ...multimedia]

    const total = allInstruments.reduce((acc, instrument) => {
      const stemsTotal = instrument.stems
        .filter((stem) => selectedInstruments.includes(stem.id))
        .reduce((stemsAcc, stem) => stemsAcc + stem.stems.length, 0)

      return acc + stemsTotal
    }, 0)

    return total
  }, [instruments, multimedia, selectedInstruments])

  const onSubmit = useCallback(() => {
    onUpdateTrack()
    setOpen(false)
    setCurrentInstruments([])
  }, [onUpdateTrack])

  const handleToggleHifi = useCallback(() => {
    if (isPro) return
    onLockedFeatureClick?.(
      'unlock-additional-tracks-hifi',
      false,
      'hi-fi-toggle'
    )
  }, [isPro, onLockedFeatureClick])

  const processingStem: string[] | undefined = useMemo(() => {
    return summary?.separate?.stems
      ?.filter((stem) => stem?.adding === true)
      ?.map((stem) => stem?.stem || '')
  }, [summary])

  const onClearSelection = useCallback(() => {
    clearInstrumentsSelected(newChangeSeparation)
  }, [clearInstrumentsSelected, newChangeSeparation])

  return (
    <div ref={ref} className={styles.container}>
      {newChangeSeparation ? (
        <Button
          opened={open}
          onClick={onToggleDrop}
          extraMarginIconDrop
          disabled={!isReadyToPlay || isProcessing}
          icon={<Icon name="spliter" />}
          text={t`upload.separate.title`}
        />
      ) : (
        <>
          {title && (
            <Button
              iconDrop
              opened={open}
              onClick={onToggleDrop}
              extraMarginIconDrop
              text={title}
              disabled={!isReadyToPlay}
            />
          )}
        </>
      )}

      <Modal
        isOpen={open}
        label="instruments-change-separate-custom-tracks"
        targetId="moises-app"
        onClose={onToggleDrop}
        modalClassName={styles.modal}
        shouldCloseOnOverlayClick
      >
        <div className="p-6 bg-gray-900">
          <header className={styles.header}>
            <h2 className="text-white text-xl font-semibold">
              {t`upload.separate.title`}
            </h2>
            <Button
              id="modal_button_close"
              className="!bg-transparent !p-0 !min-w-0 h-0"
              icon={<Icon name="close" />}
              onClick={onToggleDrop}
              aria-label="Fechar"
            />
          </header>

          <div>
            {instruments && instruments?.length > 0 ? (
              <>
                <div className={styles.stems}>
                  <SeparateCustom
                    openModal={onLockedFeatureClick}
                    methods={methods}
                    processing={isProcessing}
                    processingStem={processingStem}
                    isLimited={isLimited}
                    isChangeOperations
                    tooltipBottom
                    newChangeSeparation={newChangeSeparation}
                    onClearSelection={onClearSelection}
                  />
                </div>
                <footer className={styles.footer}>
                  {isLimited ? (
                    <p className="text-xs font-medium text-[#d1d1d1]">
                      {i18n
                        ._(t`selected_tracks_count`)
                        .replace('**Tracks**', tracksSelected.toString())
                        .replace('**Limit**', '5')}
                    </p>
                  ) : (
                    <span />
                  )}

                  <div className="flex items-center gap-6">
                    <ToogleHifi
                      handleToggleHifi={handleToggleHifi}
                      isPro={!!isPro}
                    />
                    {newChangeSeparation ? (
                      <ButtonScala
                        small
                        stroke
                        radius={1}
                        title={t`edit.playlist.save`}
                        onClick={onSubmit}
                        disabled={!isValidTracksSelected}
                        order="right"
                        icon={
                          <Icon name="check-simple" className="rotate-180" />
                        }
                        className="!px-[41px] !py-1"
                      />
                    ) : (
                      <ButtonScala
                        small
                        stroke
                        radius={1}
                        onClick={onSubmit}
                        disabled={
                          !instruments.length ||
                          isProcessing ||
                          !isValidTracksSelected ||
                          selectedInstruments.length === 0 ||
                          (selectedInstruments.length === 1 &&
                            selectedInstruments[0] === 'other')
                        }
                        title={i18n._(t`submit`)}
                        className={classNames(
                          styles.button,
                          '!px-[41px] !py-1',
                          {
                            [styles.loading]: loading,
                            [styles.processing]: isProcessing
                          }
                        )}
                        order="right"
                        icon={
                          <Icon name="check-simple" className="rotate-180" />
                        }
                      />
                    )}
                  </div>
                </footer>
              </>
            ) : (
              <Loading />
            )}
          </div>
        </div>
      </Modal>
    </div>
  )
}
